/**
 * Admin Panel Component
 * Platform admin dashboard for managing tenants and invitations
 */

import React, { useState } from 'react';
import TopBar from '../TopBar';
import { TenantManagement } from './TenantManagement';
import { InviteManagement } from './InviteManagement';
import { PlatformAdminManagement } from './PlatformAdminManagement';
import { AgentFleet } from './AgentFleet';
import TenantSwitcher from './TenantSwitcher';
import { getApiBaseUrl } from '../../config/environment';


interface AdminPanelProps {
  accessToken: string;
  userEmail: string;
  tenantName: string;
  tenantId: string;
}

type AdminTab = 'tenants' | 'invites' | 'admins' | 'agents';

const AdminPanel: React.FC<AdminPanelProps> = ({
  accessToken,
  userEmail,
  tenantName,
  tenantId
}) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('tenants');

  return (
    <div className="min-h-screen bg-surface-app">
      <TopBar tenantName={tenantName} userEmail={userEmail} />

      <div className="px-8 py-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight mb-1">Platform Administration</h1>
          <p className="text-body text-gray-500">Manage tenants, invitations, and platform administrators</p>
        </div>

        {/* Tenant Switcher */}
        <TenantSwitcher accessToken={accessToken} apiBaseUrl={getApiBaseUrl()} />

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 mb-6">
          {([
            { key: 'tenants', label: 'Tenants', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
            { key: 'invites', label: 'Invitations', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
            { key: 'admins', label: 'Platform Admins', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
            { key: 'agents', label: 'Agents', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
          ] as const).map(({ key, label, icon }) => (
            <button key={key} type="button" onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-5 py-3 text-label font-medium border-b-2 transition-colors duration-fast -mb-px ${
                activeTab === key ? 'border-blue-500 text-blue-500' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
              </svg>
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'tenants' && <TenantManagement accessToken={accessToken} tenantId={tenantId} />}
          {activeTab === 'invites' && <InviteManagement accessToken={accessToken} tenantId={tenantId} />}
          {activeTab === 'admins' && <PlatformAdminManagement accessToken={accessToken} />}
          {activeTab === 'agents' && <AgentFleet accessToken={accessToken} />}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;

